import React, { createContext, useContext, useEffect, useRef } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../components/ui-primitives/dropdown-menu"
import { Button } from "../../components/ui-primitives/button"
import { mergeClasses } from "../../helpers/styling"
import { DynamicIcon } from "../../components/DynamicIcon"
import { ConfirmationConfig } from "./action-button-schema"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "../../components/ui-primitives/popover"
import { ActionButtonRenderer } from "./action-button-renderer"
import { Dialog, DialogClose, DialogContent } from "../../components/ui-primitives/dialog"
import { CheckIcon, ChevronDownIcon, Loader2Icon, XIcon } from "lucide-react"
import { useWidgetColorsCssVars } from "../../components/styling/use-widget-styling"

/**
 * Tool button primitives: can be used stand-alone (directly via React) or
 * mapped from a buttons schema for configurable widgets {@link ActionButtonRenderer}
 */

type SvgIconType = React.ElementType<React.RefAttributes<SVGSVGElement>>

export type SizeVariant = "large" | "default" | "medium" | "compact"
export const SizeVariantContext = createContext<SizeVariant>("default")
export const useSizeVariant = () => useContext(SizeVariantContext)

export function HorizontalSeparator() {
  return (
    <div className={mergeClasses("my-0 mx-1 min-h-full border-l cursor-default border-[var(--w-border-color)]")} />
  )
}

export function VerticalSeparator() {
  return (
    <div className={mergeClasses("my-1 mx-[-4px] min-w-full border-b border-[var(--w-border-color)]")} />
  )
}

export function DropdownToolButton(props: {
  icon?: string | SvgIconType
  title?: string
  label?: string
  withChevron?: boolean
  children?: React.ReactNode
  contentClassName?: string
  contentStyle?: React.CSSProperties
  loading?: boolean
}) {
  const colorsCssVars = useWidgetColorsCssVars()
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
        <ToolButton icon={props.icon}
                    loading={props.loading}
                    aria-label={props.label}
                    labeled={Boolean(props.label)}>
          <span className="sr-only">{props.title ?? "Open menu"}</span>
          {props.label && <span>{props.label}</span>}
          {props.withChevron && (
            <ChevronDownIcon size={14} />
          )}
        </ToolButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end"
                           sideOffset={0}
                           style={{ ...colorsCssVars, ...props.contentStyle }}
                           className={mergeClasses("bg-[var(--w-bg-color)] !border-[var(--w-border-color)]", props.contentClassName)}>
        {props.children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function menuItemClassNames(size: SizeVariant, disabled: boolean | undefined) {
  return mergeClasses([`
      flex gap-2
      !text-[var(--w-text-color)]
      active:ring-[var(--w-ring-color)]
      data-[state=open]:text-[var(--w-hover-text-color)]
      data-[state=open]:bg-[var(--w-hover-bg-color)]
      hover:!text-[var(--w-hover-text-color)]
      hover:!bg-[var(--w-hover-bg-color)]
      `], {
    "cursor-pointer": !disabled,
    "text-xs": size === "compact",
    "text-[13px]": size === "medium",
    "text-[15px]": size === "large"
  })
}

function MenuItemIcon(props: {
  icon?: string | SvgIconType
}) {
  const size = useSizeVariant()
  const iconSize = (() => {
    switch (size) {
      case "compact": return 12
      case "medium": return 12
      case "large": return 14
      default: return 12
    }
  })()
  return (
    <>
      {props.icon ? (
        <div className="flex items-center justify-center h-4 w-4">
          {renderIcon({ icon: props.icon, size: iconSize })}
        </div>
      ) : (
        <div className="min-h-4 min-w-4" />
      )}
    </>
  )
}

export const DropdownToolItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuItem> & {
  icon?: string | SvgIconType
  onSelect?: (e: Event) => void
  children?: React.ReactNode
  disabled?: boolean
}>(function (props, ref) {
  const { icon, onSelect, ...buttonProps } = props
  const size = useSizeVariant()
  return (
    <DropdownMenuItem
      ref={ref}
      {...buttonProps}
      aria-label={props["aria-label"] ?? props.title}
      onSelect={props.onSelect}
      disabled={props.disabled}
      className={menuItemClassNames(size, props.disabled)}>
      <MenuItemIcon icon={props.icon} />
      <div className="flex-grow">
        {props.children}
      </div>
    </DropdownMenuItem>
  )
})

export const DropdownToolSubmenu = React.forwardRef<
  React.ElementRef<typeof DropdownMenuSub>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuSub> & {
  icon?: string | SvgIconType
  label?: React.ReactNode
  children?: React.ReactNode
  disabled?: boolean
  contentClassName?: string
  contentStyle?: React.CSSProperties
}>(function (props, ref) {
  const { icon, label, children, disabled, contentClassName, contentStyle, ...buttonProps } = props
  const size = useSizeVariant()
  const colorsCssVars = useWidgetColorsCssVars()
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        ref={ref}
        {...buttonProps}
        disabled={props.disabled}
        className={menuItemClassNames(size, props.disabled)}>
        <MenuItemIcon icon={props.icon} />
        <div className="flex-grow">
          {props.label}
        </div>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent style={{ ...colorsCssVars, ...props.contentStyle }}
                              className={mergeClasses("bg-[var(--w-bg-color)] !border-[var(--w-border-color)]", props.contentClassName)}>
        {props.children}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
})

export const ToolButton = React.forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & {
    icon?: string | SvgIconType
    disabled?: boolean
    labeled?: boolean
    animated?: boolean
    loading?: boolean
  }
>(function ToolButton(props, ref) {
  const { icon, labeled, animated, loading, ...buttonProps } = props
  const size = useSizeVariant()
  const iconSize = (() => {
    switch (size) {
      case "compact": return 14
      case "medium": return 16
      case "large": return 20
      default: return 18
    }
  })()
  return (
    <Button ref={ref}
            {...buttonProps}
            aria-label={buttonProps["aria-label"] ?? props.title}
            disabled={props.disabled || props.loading}
            variant="ghost"
            className={mergeClasses(`
              flex gap-1 p-0 items-center
              rounded-md
              active:ring-2 active:ring-[var(--w-ring-color)]
              text-[var(--w-text-color)]
              hover:text-[var(--w-hover-text-color)]
              hover:bg-[var(--w-hover-bg-color)]
            `, {
              "active:scale-95 transition-transform": props.animated,
              "active:scale-[98%]": props.animated && size === "large",
              "px-2": props.labeled,
              "h-9 min-w-9 text-[15px] gap-2": size === "large",
              "h-8 min-w-8": size === "default",
              "h-7 min-w-7 text-[13px]": size === "medium",
              "h-6 min-w-6 text-xs": size === "compact"
            }, props.className)}>
      {props.loading && (
        <Loader2Icon className="animate-spin" size={Math.floor(iconSize)} />
      )}
      {props.icon && !props.loading && renderIcon({
        icon: props.icon,
        size: iconSize
      })}
      {props.children}
    </Button>
  )
})

export function ConfirmationDialog(props: {
  config: ConfirmationConfig
  open?: boolean
  onConfirmed: () => void
  onClose: () => void
  title?: string
}) {
  // Workaround for Radix DropdownMenu (used by Popover) trying to steal back focus,
  // which in the current version they are also triggering by a setTimeout (without a ms argument)
  const buttonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!props.open) return
    setTimeout(() => {
      buttonRef.current?.focus()
    }, 100)
  }, [ props.open ])

  return (
    <Dialog open={props.open}
            onOpenChange={open => !open && props.onClose()}>
      <DialogContent autoFocus
                     className="flex flex-col gap-4 items-center">
        {props.config.message}
        <div className="flex justify-center gap-2">
          <DialogClose asChild>
            <Button ref={buttonRef}
                    variant="outline"
                    className="gap-1 items-center"
                    aria-label={`confirm ${props.title}`}
                    tabIndex={0}
                    onClick={() => props.onConfirmed?.()}
                    style={{ color: props.config.yesColor || '#78c1a3' }}>
              {props.config.yesIcon
                ? <DynamicIcon size="16px" icon={props.config.yesIcon} />
                : <CheckIcon size={16}  />}
              {props.config.yesLabel ?? "Yes"}
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant="outline"
                    className="gap-1 items-center"
                    aria-label={`cancel ${props.title}`}
                    tabIndex={1}
                    style={{ color: props.config.noColor || '#f38989' }}>
              {props.config.noIcon
                ? <DynamicIcon size="16px" icon={props.config.noIcon} />
                : <XIcon size={16} />}
              {props.config.noLabel ?? "No"}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ConfirmationPopover(props: {
  config: ConfirmationConfig
  onConfirmed?: () => void
  children: React.ReactNode
  title?: string
}) {
  if (!props.config.message) return props.children

  return (
    <Popover>
      <PopoverTrigger asChild onClick={e => e.stopPropagation()}>
        {props.children}
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col w-fit text-center p-3 gap-2">
        {props.config.message}
        <div className="flex flex-row justify-center gap-2">
          <PopoverClose asChild autoFocus>
            <Button variant="outline"
                    className="gap-1 items-center"
                    aria-label={`confirm ${props.title ?? ''}`}
                    autoFocus
                    style={{ color: props.config.yesColor || '#78c1a3' }}
                    onClick={e => {
                      e.stopPropagation()
                      props.onConfirmed?.()
                    }}>
              {props.config.yesIcon
                ? <DynamicIcon size="16px" icon={props.config.yesIcon} />
                : <CheckIcon size={16}  />}
              {props.config.yesLabel ?? "Yes"}
            </Button>
          </PopoverClose>
          <PopoverClose asChild>
            <Button variant="outline"
                    className="gap-1 items-center"
                    aria-label={`cancel ${props.title}`}
                    style={{ color: props.config.noColor || '#f38989' }}
                    onClick={e => e.stopPropagation() }>
              {props.config.noIcon
                ? <DynamicIcon size="16px" icon={props.config.noIcon} />
                : <XIcon size={16} />}
              {props.config.noLabel ?? "No"}
            </Button>
          </PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function ToolHeader(props: {
  logo?: string
  title?: string
  color?: string
}) {
  const size = useSizeVariant()
  const iconSize = (() => {
    switch (size) {
      case "compact": return 14
      case "medium": return 16
      case "large": return 20
      default: return 18
    }
  })()
  return (
    <div className={mergeClasses(
      "flex px-1 gap-2 items-center font-bold select-none", {
        "text-[15px]": size === "large",
        "text-sm": size === "default",
        "text-[13px]": size === "medium",
        "text-xs": size === "compact"
      })}
      style={{
        color: props.color ?? "var(--w-text-color)"
      }}>
      {props.logo && (
        <img src={props.logo}
             style={{ height: iconSize }}
             alt={`${props.title} logo`} />
      )}
      {props.title}
    </div>
  )
}

function renderIcon(args: {
  icon: string | SvgIconType,
  size?: number
}) {
  if (typeof args.icon === "string") {
    return <DynamicIcon icon={args.icon} size={`${args.size}px`} />
  } else {
    const SvgIcon = args.icon as SvgIconType
    return <SvgIcon width={args.size} height={args.size} />
  }
}
